import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { ApiPaths, api } from "@/lib/api";
import { useConsentStore } from "@/lib/consent";
import { setFieldSessionToken } from "@/lib/fieldSession";
import type { GeoFix } from "@/lib/geolocation";
import type { FieldJoinPayload } from "@/lib/types";
import { ConsentScreen } from "@/modules/consent";
import { FieldRoom } from "@/modules/room";

/**
 * /join/:sessionId?t={token} — field-user landing.
 *
 * The backend issues invite URLs of this exact shape — see
 * `apps/sessions/services.py::issue_invite`. The signed token IS the auth
 * factor; the page is unauthenticated by design.
 *
 * When the operator issued the invite with `recipient_email` or
 * `recipient_phone`, an OTP is also required: the field user enters the
 * 6-digit code they received separately, and the SPA resends the join
 * request with `X-Join-OTP` set. The OTP travels in a header rather than
 * the URL so it doesn't end up in browser history or access logs.
 *
 * AGENTS.md §17: this route MUST NOT attach a Bearer header. The api.ts
 * request interceptor inspects the URL path and skips the header for any
 * call matching `/sessions/{id}/join/`.
 */
type OtpRequiredResponse = {
  detail: "otp_required";
  channels: string[];
  channel_hint_email?: string;
  channel_hint_phone?: string;
};


export default function JoinPage() {
  const { t } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [params] = useSearchParams();
  const token = params.get("t");
  const setConsent = useConsentStore((s) => s.set);
  const [proceeding, setProceeding] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [otpState, setOtpState] = useState<{
    needed: boolean;
    hint: OtpRequiredResponse | null;
    code: string;
    attemptsRemaining: number | null;
    expired: boolean;
    locked: boolean;
    wrong: boolean;
  }>({
    needed: false,
    hint: null,
    code: "",
    attemptsRemaining: null,
    expired: false,
    locked: false,
    wrong: false,
  });

  // Pre-join org-policy peek (no side effects) so the consent gate knows
  // whether a GPS fix is mandatory BEFORE the field user commits. Fails
  // safe: if the peek errors, we treat geo as required (the default policy).
  const joinInfo = useQuery({
    queryKey: ["join-info", sessionId, token],
    queryFn: async () =>
      (
        await api.get<{ require_field_geo: boolean; session_status: string }>(
          ApiPaths.sessionJoinInfo(sessionId!, token!),
        )
      ).data,
    enabled: !!sessionId && !!token,
    staleTime: Infinity,
    retry: false,
  });

  /** Attach a GPS fix to the session. Non-essential by design — the
   *  inspection proceeds without one and the report flags it as not
   *  captured — so every failure is swallowed. Requires the invite to
   *  already be redeemed: the field-session-token is what authenticates
   *  this POST (2026-05-17 — posting before redemption 401'd). */
  const postGeo = async (geo: GeoFix) => {
    if (!sessionId) return;
    try {
      await api.post(ApiPaths.sessionGeo(sessionId), {
        lat: geo.lat,
        lng: geo.lng,
        accuracy_m: geo.accuracy_m,
        source: geo.source,
        timestamp: geo.timestamp,
        ...(geo.altitude_m !== null ? { altitude_m: geo.altitude_m } : {}),
        ...(geo.altitude_accuracy_m !== null
          ? { altitude_accuracy_m: geo.altitude_accuracy_m }
          : {}),
      });
    } catch {
      /* Geo write is non-essential. */
    }
  };

  const join = useMutation({
    mutationFn: async (otpCode?: string) => {
      if (!sessionId || !token) throw new Error("missing session id or token");
      const headers = otpCode ? { "X-Join-OTP": otpCode } : undefined;
      const res = await api.get<FieldJoinPayload>(
        ApiPaths.sessionJoin(sessionId, token),
        { headers },
      );
      // Persist the short-lived token so FieldCaptureHandler can read it.
      // Cleared on tab close — never localStorage.
      setFieldSessionToken(res.data.field_session_token);
      return res.data;
    },
  });

  if (!sessionId || !token) {
    return (
      <main className="mx-auto max-w-md p-6 text-nb-text">
        <h1 className="text-xl font-semibold">{t("join.linkIncomplete")}</h1>
        <p className="mt-2 text-sm text-nb-text-2">
          {t("join.linkIncompleteBody")}
        </p>
      </main>
    );
  }

  if (!proceeding) {
    return (
      <ConsentScreen
        requireGeo={joinInfo.data?.require_field_geo ?? true}
        // Backlog 2026-09-22 — the consent gate no longer blocks the
        // join on a slow GPS fix. If one lands after we've already gone
        // through, attach it then rather than losing it. Safe to fire
        // late: by this point the invite has been redeemed, so the
        // field-session-token the POST needs is stamped.
        onLateGeo={(late) => {
          void postGeo(late);
        }}
        onProceed={async ({ consents: snap, geo }) => {
          setConsent("join", snap.join ?? "denied");
          setConsent("audio", snap.audio ?? "denied");
          setConsent("video", snap.video ?? "denied");
          // CRITICAL ORDER (fix 2026-05-17): redeem the invite FIRST so
          // the field-session-token is stamped, THEN POST geo. Reversing
          // this sent the geo POST with NO auth header and 401'd.
          let joined: FieldJoinPayload | null = null;
          try {
            joined = await join.mutateAsync(undefined);
          } catch (err: unknown) {
            // Inspect the response. If the backend asked for an OTP,
            // surface the entry form INSTEAD of bailing to the
            // generic error screen.
            const resp = (err as { response?: { status: number; data?: unknown } })
              .response;
            const data = resp?.data as
              | { detail?: string; attempts_remaining?: number }
              | undefined;
            if (resp?.status === 401 && data?.detail === "otp_required") {
              setOtpState((s) => ({
                ...s,
                needed: true,
                hint: data as unknown as OtpRequiredResponse,
              }));
              setProceeding(true);
              return;
            }
            // Operator already ended the session — the signed token is
            // still valid but the backend refuses to mint a camera token.
            // Show a clean "ended" screen instead of the generic error.
            if (resp?.status === 409 && data?.detail === "session_ended") {
              setSessionEnded(true);
              setProceeding(true);
              return;
            }
            setProceeding(true);
            return;
          }
          if (geo) await postGeo(geo);
          setProceeding(true);
          void joined;
        }}
      />
    );
  }

  // Session already ended — show a terminal "ended" screen instead of
  // ever mounting FieldRoom (which would open the camera). Set from the
  // backend's 409 `session_ended`, plus a belt-and-suspenders check on
  // the payload status in case a stale token still returns one.
  const terminalStatuses = ["closed", "expired", "cancelled"];
  if (
    sessionEnded ||
    (join.data && terminalStatuses.includes(join.data.session_status))
  ) {
    return (
      <main className="mx-auto max-w-md p-6 text-nb-text">
        <h1 className="text-xl font-semibold">{t("join.endedTitle")}</h1>
        <p className="mt-2 text-sm text-nb-text-2">{t("join.endedBody")}</p>
      </main>
    );
  }

  // OTP gate — first request returned otp_required. Render an entry
  // form and re-submit with the header.
  if (otpState.needed && !join.data) {
    return (
      <main className="mx-auto max-w-md p-6 text-nb-text">
        <h1 className="text-xl font-semibold">{t("join.otpTitle")}</h1>
        <p className="mt-2 text-sm text-nb-text-2">
          {t("join.otpBody")}
        </p>
        {otpState.hint && (
          <p className="mt-3 text-xs text-nb-text-3">
            {otpState.hint.channels.includes("email") && otpState.hint.channel_hint_email && (
              <>📧 {otpState.hint.channel_hint_email}</>
            )}
            {otpState.hint.channels.includes("email") &&
              otpState.hint.channels.includes("sms") &&
              " · "}
            {otpState.hint.channels.includes("sms") && otpState.hint.channel_hint_phone && (
              <>📱 {otpState.hint.channel_hint_phone}</>
            )}
          </p>
        )}
        <form
          className="mt-5 space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const code = otpState.code.replace(/\s+/g, "");
            if (code.length !== 6) return;
            try {
              await join.mutateAsync(code);
              setOtpState((s) => ({
                ...s,
                wrong: false,
                expired: false,
                locked: false,
              }));
            } catch (err: unknown) {
              const resp = (err as { response?: { status: number; data?: unknown } })
                .response;
              const data = resp?.data as
                | { detail?: string; attempts_remaining?: number }
                | undefined;
              if (resp?.status === 423 || data?.detail === "otp_locked") {
                setOtpState((s) => ({ ...s, locked: true }));
              } else if (data?.detail === "otp_expired") {
                setOtpState((s) => ({ ...s, expired: true }));
              } else if (data?.detail === "otp_invalid") {
                setOtpState((s) => ({
                  ...s,
                  wrong: true,
                  code: "",
                  attemptsRemaining: data.attempts_remaining ?? null,
                }));
              }
            }
          }}
        >
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            autoComplete="one-time-code"
            className="input w-full text-center font-mono text-2xl tracking-[0.4em]"
            placeholder="000000"
            value={otpState.code}
            onChange={(e) =>
              setOtpState((s) => ({
                ...s,
                code: e.target.value.replace(/\D/g, "").slice(0, 6),
              }))
            }
            disabled={otpState.locked || otpState.expired}
            autoFocus
          />
          {otpState.wrong && (
            <p className="text-xs text-cvp-danger">
              {t("join.otpWrong")}
              {otpState.attemptsRemaining !== null && (
                <> {t("join.otpAttemptsRemaining", { count: otpState.attemptsRemaining })}</>
              )}
            </p>
          )}
          {otpState.expired && (
            <p className="text-xs text-amber-400">{t("join.otpExpired")}</p>
          )}
          {otpState.locked && (
            <p className="text-xs text-cvp-danger">{t("join.otpLocked")}</p>
          )}
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={
              otpState.code.length !== 6 ||
              otpState.locked ||
              otpState.expired ||
              join.isPending
            }
          >
            {join.isPending ? t("join.otpVerifying") : t("join.otpSubmit")}
          </button>
        </form>
      </main>
    );
  }

  if (join.isError && !otpState.needed) {
    return (
      <main className="mx-auto max-w-md p-6 text-nb-text">
        <h1 className="text-xl font-semibold">{t("join.errorTitle")}</h1>
        <p className="mt-2 text-sm text-cvp-danger">
          {t("join.errorBody")}
        </p>
      </main>
    );
  }

  if (!join.data) {
    return <p className="p-6 text-nb-text">{t("join.connecting")}</p>;
  }

  return (
    <FieldRoom
      token={join.data.livekit.token}
      url={join.data.livekit.url}
      sessionId={join.data.session_id}
      fieldSessionToken={join.data.field_session_token}
      sessionStatus={join.data.session_status}
      recordingStartedAt={join.data.recording_started_at}
    />
  );
}
